/**
 * Client for the AutoMom goal-progress review prompts API.
 *
 * AutoMom creates a prompt when a user completes a task that belongs to a
 * goal.  The user is offered three choices:
 *  - mark_complete    → AutoMom marks the goal done in the life graph
 *  - request_breakdown → navigate to AI Breakdown / goal journey
 *  - keep_going / not_now → dismiss
 */

const BASE = '/api/automom/v1/goals'

export interface GoalReviewPrompt {
  id: string
  goal_id: number
  goal_title: string
  completed_task_id: number
  completed_task_title: string
  remaining_tasks_count: number
  total_tasks_count: number
  all_tasks_done: boolean
  status: 'available' | 'shown' | 'responded' | 'dismissed'
  response_code: string | null
  expires_at: string
  created_at: string
}

export type GoalReviewResponseCode =
  | 'mark_complete'
  | 'request_breakdown'
  | 'keep_going'
  | 'not_now'

export interface GoalReviewRespondResult {
  success: boolean
  response_code: GoalReviewResponseCode
  goal_id: number
  all_tasks_done: boolean
  next_step: 'goal_completed' | 'breakdown' | null
  goal_marked_complete?: boolean
  error?: string
}

async function getReviewPrompts(surface = 'today'): Promise<{
  prompts: GoalReviewPrompt[]
  prompt: GoalReviewPrompt | null
}> {
  const res = await fetch(`${BASE}/review-prompts?surface=${surface}`, {
    credentials: 'include',
  })
  if (!res.ok) return { prompts: [], prompt: null }
  const data = await res.json()
  return { prompts: data.prompts ?? [], prompt: data.prompt ?? null }
}

async function markShown(promptId: string): Promise<void> {
  await fetch(`${BASE}/review-prompts/${promptId}/shown`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => undefined)
}

async function respond(
  promptId: string,
  responseCode: GoalReviewResponseCode,
): Promise<GoalReviewRespondResult> {
  const res = await fetch(`${BASE}/review-prompts/${promptId}/respond`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ response_code: responseCode }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    return {
      success: false,
      response_code: responseCode,
      goal_id: 0,
      all_tasks_done: false,
      next_step: null,
      error: (data && data.error) || `http_${res.status}`,
    }
  }
  return data as GoalReviewRespondResult
}

export const goalReviewService = { getReviewPrompts, markShown, respond }
